import { useEffect, useRef, useState } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * Registers the service worker and subscribes to Web Push after join.
 * Sends the subscription to the backend so we can dispatch VAPID pushes.
 */
export function usePushSubscription(ticketUuid, shopId, active) {
  const [state, setState] = useState('idle'); // idle | granted | denied | unsupported
  const sentRef = useRef(false);

  useEffect(() => {
    if (!active || !ticketUuid || !shopId || sentRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported');
      return;
    }

    let cancelled = false;

    async function subscribe() {
      try {
        // 1. Get VAPID public key from backend
        const keyRes = await fetch(`${API_BASE}/api/v1/push/vapid-public-key`);
        if (!keyRes.ok) return;
        const { publicKey } = await keyRes.json();

        // 2. Register service worker
        const reg = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        // 3. Request permission
        const permission = await Notification.requestPermission();
        if (cancelled) return;
        if (permission !== 'granted') { setState('denied'); return; }

        // 4. Subscribe to push
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
        if (cancelled) return;

        const json = sub.toJSON();
        await fetch(`${API_BASE}/api/v1/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketUuid,
            shopId,
            endpoint: json.endpoint,
            p256dh:   json.keys.p256dh,
            authKey:  json.keys.auth,
          }),
        });

        sentRef.current = true;
        setState('granted');
      } catch (err) {
        console.warn('Push subscription failed:', err);
      }
    }

    subscribe();
    return () => { cancelled = true; };
  }, [active, ticketUuid, shopId]);

  return state;
}
